import { z } from 'zod'
import { ufList } from 'src/utils/masks'

import { validateCPF } from '../utils'

export const emailSchema = z.object({
  email: z.string().email('Digite um e-mail válido (ex: seuemail@email.com)'),
})

export const profileSchema = z.object({
  name: z
    .string()
    .refine(
      (val) => {
        const words = val.trim().split(/\s+/)

        return words.length >= 2
      },
      {
        message: 'Preencha seu nome completo',
      }
    )
    .refine(
      (val) => {
        const words = val.trim().split(/\s+/)

        return words.every((word) => word.length >= 2)
      },
      {
        message: 'Digite um nome válido com pelo menos 2 letras',
      }
    )
    .refine((val) => /^[\p{L}\s]+$/u.test(val), {
      message: 'O nome não deve conter caracteres especiais ou números',
    })
    .refine(
      (val) => {
        const clearedVal = val.replace(/\s+/g, '')

        return !/^(.)\1+$/.test(clearedVal)
      },
      {
        message: 'Digite um nome válido',
      }
    ),
  cpf: z
    .string()
    .refine((val) => val.trim() !== '', {
      message: 'Informe seu CPF',
    })
    .refine(
      (val) => {
        const numbers = val.replace(/[^\d]/g, '')

        return numbers.length === 11
      },
      {
        message: 'O CPF está incompleto',
      }
    )
    .refine(
      (val) => {
        return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(val)
      },
      {
        message: 'O formato do CPF está incorreto',
      }
    )
    .refine(validateCPF, {
      message: 'Digite um CPF válido',
    })
    .refine(
      (val) => {
        const numbers = val.replace(/[^\d]/g, '')

        return !/^(\d)\1{10}$/.test(numbers)
      },
      {
        message: 'Este CPF não é válido',
      }
    ),
  phone: z
    .string()
    .refine((val) => val.trim() !== '', {
      message: 'Informe seu número de celular',
    })
    .refine(
      (val) => {
        const numbers = val.replace(/[^\d]/g, '')

        return numbers.length >= 10 && numbers.length <= 11
      },
      {
        message: 'O número está incompleto',
      }
    ),
  newsletterOptin: z.boolean().optional(),
})

export const addressSchema = z.object({
  number: z
    .string()
    .min(1, { message: 'Informe o número do endereço' })
    .refine(
      (val) => {
        return /^[a-zA-Z0-9\s]+$/.test(val) && val.length <= 10
      },
      {
        message: 'Digite um número válido',
      }
    ),
  complement: z
    .string()
    .max(30, {
      message: 'O complemento deve ter no máximo 30 caracteres',
    })
    .refine(
      (val) => {
        if (val === undefined || val.trim() === '') {
          return true
        }

        return /^[a-zA-Z0-9\s]+$/.test(val.trim())
      },
      {
        message: 'Digite um complemento válido ou deixe em branco',
      }
    )
    .optional(),
  receiverName: z
    .string()
    .refine((val) => val.trim() !== '', {
      message: 'Informe o nome do destinatário',
    })
    .refine((val) => val.trim().length >= 3, {
      message: 'Digite um nome válido com pelo menos 3 letras',
    })
    .refine(
      (val) => {
        return /^[a-zA-ZÀ-ÿ\s]+$/.test(val)
      },
      {
        message: 'O nome deve conter apenas letras e espaços',
      }
    ),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  neighborhood: z.string().optional(),
  reference: z.string().optional(),
})

export const createAddressSchema = (optionalFields: string[]) => {
  const baseSchema = z.object({
    number: z
      .string()
      .trim()
      .min(1, { message: 'Informe o número do endereço' })
      .refine((val) => /^[a-zA-Z0-9\s]+$/.test(val) && val.length <= 10, {
        message: 'Digite um número válido',
      }),
    complement: z
      .string()
      .trim()
      .max(30, { message: 'O complemento deve ter no máximo 30 caracteres' })
      .refine(
        (val) => {
          if (val === undefined || val.trim() === '') {
            return true
          }

          return /^[a-zA-Z0-9\s]+$/.test(val.trim())
        },
        { message: 'Digite um complemento válido ou deixe em branco' }
      )
      .optional(),
    receiverName: z
      .string()
      .trim()
      .refine((val) => val.trim() !== '', {
        message: 'Informe o nome do destinatário',
      })
      .refine((val) => val.trim().length >= 3, {
        message: 'Digite um nome válido com pelo menos 3 letras',
      })
      .refine((val) => /^[a-zA-ZÀ-ÿ\s]+$/.test(val), {
        message: 'O nome deve conter apenas letras e espaços',
      }),
    reference: z.string().optional(),
  })

  const optionalFieldsSchema = {
    street: z.string().trim().min(1, { message: 'Informe a rua' }),
    city: z.string().trim().min(1, { message: 'Informe a cidade' }),
    state: z.string().trim().min(1, { message: 'Informe o estado' }),
    neighborhood: z.string().trim().min(1, { message: 'Informe o bairro' }),
  }

  return optionalFields.reduce<z.ZodObject<any>>((schema, field) => {
    if (optionalFieldsSchema[field as keyof typeof optionalFieldsSchema]) {
      if (field in optionalFieldsSchema) {
        return schema.extend({
          [field]:
            optionalFieldsSchema[field as keyof typeof optionalFieldsSchema],
        })
      }

      return schema
    }

    return schema
  }, baseSchema)
}

export const BRAZILIAN_STATES = ufList.filter((uf) => uf !== 'Selecione')

export interface NewAddressFormValues {
  street: string
  neighborhood: string
  city: string
  state: string
  number: string
  complement?: string
  receiverName: string
}

export const createNewAddressSchema = (editableFields: string[]) => {
  const schema = createAddressSchema(editableFields)

  if (!editableFields.includes('state')) {
    return schema
  }

  return schema.extend({
    state: z
      .string()
      .trim()
      .min(1, { message: 'Informe o estado' })
      .refine((val) => BRAZILIAN_STATES.includes(val), {
        message: 'Selecione um estado válido',
      }),
  })
}
